import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../../lib/supabase';
import Sidebar from '../../admin-dashboard/components/Sidebar';
import AdminBottomNav from '../../admin-dashboard/components/AdminBottomNav';
import AdminMobileHeader from '../../admin-dashboard/components/AdminMobileHeader';
import { useTenant } from '../../../contexts/TenantContext';
import { useRole } from '../../../hooks/useRole';
import TenantRulesSection from './components/TenantRulesSection';
import AgencyOverridesSection from './components/AgencyOverridesSection';
import RegenerateTargetsButton from './components/RegenerateTargetsButton';

/**
 * Admin — Objective Rules console (`/admin/objective-rules`).
 *
 * Chantier 2.5: UI for tenant_admin / agency_owner / agency_admin (and super_admin)
 * to manage `tenant_objective_rules` and (gated by flag) `agency_objective_overrides`,
 * and to trigger monthly target regeneration via `apply_tenant_objective_rules` RPC.
 *
 * Roles & visibility:
 *   - super_admin / tenant_admin → can edit tenant rules + see all agencies' overrides
 *   - agency_owner / agency_admin → can edit overrides for their own agency only
 *   - agency_owner / agency_admin without the `allow_agency_target_override` flag
 *     → see only the explainer block in section 2
 *
 * Regenerate button: only super_admin and tenant_admin (and agency_owner if RLS allows)
 * are expected to be able to call the RPC. We display the button to the same group as
 * tenant rules edit; RLS errors surface inline.
 */
const AdminObjectiveRulesPage = () => {
  const { t } = useTranslation();
  const {
    tenantId,
    tenantSlug,
    tenantName,
    agencyId,
    agencyName,
    permissionMatrix,
    userRole,
    loading: tenantLoading,
  } = useTenant();
  const isSuperAdmin = useRole('super_admin');
  const isTenantAdmin = useRole('tenant_admin');
  const isAgencyOwner = useRole('agency_owner');
  const isAgencyAdmin = useRole('agency_admin');

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [userEmail, setUserEmail] = useState('');

  // Hydrate email for sidebar
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user?.email) setUserEmail(session.user.email);
    });
  }, []);

  // Cached tenant rules used to display "inherited from tenant" annotation in overrides
  const [tenantRules, setTenantRules] = useState([]);
  const refreshTenantRules = useCallback(async () => {
    if (!tenantId) return;
    const { data } = await supabase
      .from('tenant_objective_rules')
      .select('metric, rule_type, rule_value')
      .eq('tenant_id', tenantId);
    setTenantRules(data || []);
  }, [tenantId]);

  useEffect(() => {
    refreshTenantRules();
  }, [refreshTenantRules]);

  const allowAgencyOverride = Boolean(
    permissionMatrix?.allow_agency_target_override
  );

  const isTenantWideAdmin = isSuperAdmin || isTenantAdmin;
  const canEditTenantRules = isTenantWideAdmin;
  const canEditAgencyOverrides =
    allowAgencyOverride &&
    (isSuperAdmin || isTenantAdmin || isAgencyOwner || isAgencyAdmin);

  // Tenant resolution issue — for super_admin without a tenant context, show banner
  if (!tenantLoading && !tenantId && !isSuperAdmin) {
    return (
      <div className="min-h-screen bg-background flex">
        <Sidebar
          collapsed={sidebarCollapsed}
          onToggle={() => setSidebarCollapsed((v) => !v)}
          userEmail={userEmail}
        />
        <main className="flex-1 min-w-0 flex flex-col">
          <AdminMobileHeader
            title={t('admin.objectiveRules.title', 'Objective Rules')}
          />
          <div className="flex-1 px-5 md:px-8 py-6">
            <div className="rounded-2xl bg-card border border-error/30 p-6 text-center">
              <div className="font-heading text-base font-semibold text-error mb-1">
                {t('admin.objectiveRules.noTenant.title', 'No tenant context')}
              </div>
              <p className="text-sm text-muted-foreground">
                {t(
                  'admin.objectiveRules.noTenant.body',
                  'Your account is not attached to a tenant. Contact your administrator.'
                )}
              </p>
            </div>
          </div>
          <AdminBottomNav />
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex">
      <Sidebar
        collapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed((v) => !v)}
        userEmail={userEmail}
      />

      <main className="flex-1 min-w-0 flex flex-col">
        <AdminMobileHeader
          title={t('admin.objectiveRules.title', 'Objective Rules')}
        />

        {/* Desktop heading */}
        <div className="hidden md:block px-8 py-6 border-b border-border bg-card">
          <div className="flex items-end justify-between gap-4">
            <div className="min-w-0">
              <nav className="text-xs text-muted-foreground mb-1 flex items-center gap-1.5">
                <Link
                  to="/admin/dashboard"
                  className="hover:text-foreground transition-colors"
                >
                  {t('admin.objectiveRules.breadcrumb.dashboard', 'Dashboard')}
                </Link>
                <span>›</span>
                <span className="text-foreground">
                  {t(
                    'admin.objectiveRules.breadcrumb.current',
                    'Objective Rules'
                  )}
                </span>
              </nav>
              <h1 className="font-heading text-2xl font-bold text-foreground">
                {t('admin.objectiveRules.title', 'Objective Rules')}
              </h1>
              <p className="text-muted-foreground text-sm mt-1">
                {t(
                  'admin.objectiveRules.subtitle',
                  'Manage tenant-level rules used to generate monthly agent targets.'
                )}
              </p>
              {(tenantName || tenantSlug) && (
                <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold mt-2">
                  {tenantName || tenantSlug}
                  {agencyName ? ` · ${agencyName}` : ''}
                </div>
              )}
            </div>

            {canEditTenantRules && tenantId && (
              <RegenerateTargetsButton tenantId={tenantId} />
            )}
          </div>
        </div>

        {/* Mobile regenerate button (separate row under mobile header) */}
        {canEditTenantRules && tenantId && (
          <div className="md:hidden px-4 py-3 bg-card border-b border-border">
            <RegenerateTargetsButton tenantId={tenantId} />
          </div>
        )}

        {/* Content */}
        <div className="flex-1 px-4 md:px-8 py-5 md:py-6 pb-24 md:pb-8 space-y-8">
          {/* Section 1 — Tenant rules */}
          {tenantId && (
            <TenantRulesSection
              tenantId={tenantId}
              canWrite={canEditTenantRules}
            />
          )}

          {/* Section 2 — Agency overrides (gated) */}
          {tenantId && allowAgencyOverride ? (
            <AgencyOverridesSection
              tenantId={tenantId}
              scopedAgencyId={isTenantWideAdmin ? null : agencyId}
              canWrite={canEditAgencyOverrides}
              isTenantWideAdmin={isTenantWideAdmin}
              tenantRules={tenantRules}
            />
          ) : (
            <section className="rounded-2xl bg-card border border-dashed border-border p-6">
              <div className="font-heading text-base font-semibold text-foreground mb-1">
                {t(
                  'admin.objectiveRules.agency.disabledTitle',
                  'Agency overrides are disabled'
                )}
              </div>
              <p className="text-sm text-muted-foreground">
                {t(
                  'admin.objectiveRules.agency.disabledBody',
                  'To allow agency-level overrides, ask your super admin to enable the flag in the tenant settings.'
                )}
              </p>
              {isSuperAdmin && tenantId && (
                <div className="mt-3">
                  <Link
                    to={`/superadmin/tenants/${tenantId}`}
                    className="inline-flex items-center gap-1 text-sm text-accent font-medium hover:underline"
                  >
                    {t(
                      'admin.objectiveRules.agency.openTenantSettings',
                      'Open tenant settings'
                    )}{' '}
                    →
                  </Link>
                </div>
              )}
            </section>
          )}

          {/* Footer hint about RLS / who can edit */}
          <div className="text-xs text-muted-foreground">
            {userRole && (
              <span>
                {t(
                  'admin.objectiveRules.signedInAs',
                  'Signed in as {{role}}.',
                  { role: userRole }
                )}{' '}
              </span>
            )}
            {!canEditTenantRules && !canEditAgencyOverrides && (
              <span>
                {t(
                  'admin.objectiveRules.readOnly',
                  'You have read-only access. Contact a tenant admin to make changes.'
                )}
              </span>
            )}
          </div>
        </div>

        <AdminBottomNav />
      </main>
    </div>
  );
};

export default AdminObjectiveRulesPage;
